<script setup>
// The "this session is full" modal. Two variants, one component, because the
// nodes are identical apart from the headline:
//   'seat-failed'  — 4669:56089, Save a Seat that couldn't be fulfilled. Covers
//                    both the already-full case and the race where the session
//                    fills DURING processing (flow 4), which lands on the same
//                    screen.
//   'already-full' — 4669:56198, Add to My Schedule on a session that is
//                    already full (flow 5).
//
// Both offer the same way out: find a replacement (which hands off to the
// existing find-a-session/gap experience) or add it to My Schedule anyway
// without a seat.
const props = defineProps({
    sessionTitle: { type: String, default: '' },
    variant: {
        type: String,
        default: 'seat-failed',
        validator: (v) => ['seat-failed', 'already-full'].includes(v),
    },
    // Both ways out of this screen assume the session is NOT on the schedule.
    // It usually is by the time we get here — claiming a seat adds the session
    // first — which left "Add to My Schedule" offering something already done.
    // It drives the notice copy as well as the button, because that copy opens
    // with "You can still add this session to My Schedule": hiding one and
    // keeping the other would contradict itself.
    //
    // As of 2026-08-20 every caller passes true — the screen is only reached from
    // the seat gesture, which adds the session before it reserves. The false
    // branch is kept for a caller that opens this without that guarantee, but
    // nothing reaches it today.
    alreadyScheduled: { type: Boolean, default: false },
});
const open = defineModel('open', { default: true });
const emit = defineEmits(['close']);

// Same close-value contract as the other dialogs: the opener needs to know
// WHICH way out was taken to continue the flow (find-a-session vs add anyway).
const result = ref(null);

const quoted = computed(() => `“${props.sessionTitle}”`);

// "Check back later to see if any seats have opened up" was the only thing on
// offer when this was written, because a full session could not be joined at all.
// It can now: Bizzabo answers a full-session request with 202 and adds the
// attendee to its standby list, so the card's own button reads "Join Waitlist".
// Telling them to check back later would send them away from an action sitting
// right behind them.
//
// Wording matches the confirmation they get after joining (the 'waitlisted'
// message in reserve-seat.js) so the two read as one flow rather than two
// descriptions of the same thing.
// One notice now, because there is one action. The old copy split on whether the
// session was already on the schedule so it could offer "add it anyway" as a
// separate way out; joining standby adds it as part of the same gesture, so that
// distinction only ever described the button label.
// Copy dictated by UNB-749 (Steph Kelch via Marker.io, 2026-09-03), used close to
// verbatim. Two corrections to the ticket's text, both flagged back to Jack:
// "add people from the waitlist to seat saved" -> "to a saved seat", and
// "join the session onsite but joining the standby line" -> "by joining".
//
// The ON-SITE STANDBY LINE keeps that name here on the ticket's own wording — it
// is the physical queue at the door, not this list.
//
// NOTE the promise this copy makes: an email confirmation when someone is moved
// off the waitlist. We do not send that; it is Bizzabo's to send. If that is not
// configured, this sentence commits us to something that will not happen.
const notice = computed(() => (
    'This session has a limited capacity and is now full. You can join the Waitlist. '
    + 'As seats become available, we will automatically add people from the waitlist '
    + 'to a saved seat. If you are moved from the waitlist, you\u2019ll receive an email '
    + 'confirmation. If you do not move off the waitlist, you may still join the session '
    + 'onsite by joining the standby line, which opens on a first-come, first-served basis.'
));

// Says what the press will DO. handleSeat adds the session when it isn't already
// on the schedule and then reserves — one gesture, two effects — so offering
// them as two buttons described a single action twice, and let someone add
// without joining the list they came here for.
const joinLabel = computed(() => (props.alreadyScheduled
    ? 'Join Waitlist'
    : 'Add to My Schedule and Join Waitlist'));

// Both variants now say the same thing, because the same thing is true and the
// same route out exists. The old seat-failed headline — "We can’t save a seat
// for X because the session is full" — led with a refusal, which read as a dead
// end at the exact moment standby became available.
const modalHeadline = computed(() => `${quoted.value} is full, but you can join the waitlist.`);

// The modal now describes an action the attendee can take, so it has to offer
// it. Joining puts them on a real Bizzabo standby list — a write against their
// ticket — which is why it is asked rather than assumed.
const handleJoin = () => {
    result.value = 'join-standby';
    open.value = false;
};

const handleFind = () => {
    result.value = 'find-session';
    open.value = false;
};

const onShellClose = () => emit('close', result.value);
</script>

<template>
    <FragmentsModalShell
        v-model:open="open"
        :title="modalHeadline"
        @close="onShellClose"
    >
        <div class="flex flex-col items-center gap-[45px] w-full">
            <h2 class="font-hubspot-serif font-normal text-[25px] sm:text-[30px] leading-[1.3] tracking-normal text-(--magenta) text-center m-0 w-full">{{ modalHeadline }}</h2>

        <!-- Same white notice as the return-visit prompt (28/25, 14px gap): the
             copy is identical across both variants in the comp. -->
        <div class="flex items-start gap-[14px] w-full text-left bg-white rounded-[10px] px-[28px] py-[25px]">
            <svg class="shrink-0 mt-[4px] size-[16px] text-(--magenta)" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="8" cy="8" r="8" fill="currentColor" />
                <path d="M8 4V9" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                <circle cx="8" cy="11.6" r="0.9" fill="white" />
            </svg>
            <p class="font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] text-(--magenta) m-0">
                {{ notice }}
            </p>
        </div>

            <div class="flex flex-wrap items-start justify-center gap-[16px]">

                <!-- Primary, filled --darker-pink. Hands off to the existing
                     find-a-session experience in gap mode, scoped to this
                     session's slot (annotation 4670:65275). The 13px sparkle is
                     the project's own AI glyph, same as Schedule Preferences. -->
                <button
                    type="button"
                    data-test="seat-full-find"
                    class="inline-flex items-center gap-[8px] rounded-[50px] px-[20px] py-[10px]
                        bg-(--darker-pink) border border-(--darker-pink) text-white
                        font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] cursor-pointer
                        transition-colors duration-200 hover:bg-(--magenta) hover:border-(--magenta)
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)"
                    @click="handleFind"
                >
                    <svg viewBox="0 0 13 13" fill="none" aria-hidden="true" class="size-[13px] shrink-0">
                        <path d="M13 8.09719C13 8.21502 12.9044 8.31057 12.7875 8.31057C10.9214 8.31057 8.71296 10.5186 8.71296 12.3843C8.71296 12.5021 8.61738 12.5977 8.50046 12.5977C8.38354 12.5977 8.28797 12.5021 8.28797 12.3843C8.28704 10.5186 6.07856 8.31057 4.2125 8.31057C4.09465 8.31057 4 8.21502 4 8.09719C4 7.97937 4.09558 7.88381 4.2125 7.88381C6.07856 7.88381 8.28704 5.67579 8.28704 3.81104C8.28704 3.69321 8.38262 3.59766 8.49954 3.59766C8.61645 3.59766 8.71203 3.69321 8.71203 3.81104C8.71203 5.67579 10.9205 7.88381 12.7866 7.88381C12.9044 7.88381 12.9991 7.97937 12.9991 8.09719H13Z" fill="currentColor" />
                        <path d="M6 3.59735C6 3.6759 5.93628 3.7396 5.85834 3.7396C4.61429 3.7396 3.14197 5.21161 3.14197 6.4554C3.14197 6.53395 3.07826 6.59766 3.00031 6.59766C2.92236 6.59766 2.85865 6.53395 2.85865 6.4554C2.85803 5.21161 1.38571 3.7396 0.141664 3.7396C0.0630993 3.7396 0 3.6759 0 3.59735C0 3.5188 0.0637179 3.45509 0.141664 3.45509C1.38571 3.45509 2.85803 1.98308 2.85803 0.73991C2.85803 0.661361 2.92174 0.597656 2.99969 0.597656C3.07764 0.597656 3.14135 0.661361 3.14135 0.73991C3.14135 1.98308 4.61367 3.45509 5.85772 3.45509C5.93628 3.45509 5.99938 3.5188 5.99938 3.59735H6Z" fill="currentColor" />
                    </svg>
                    Find New Session
                </button>

                <!-- The affirmative, SECOND and outlined. Same shape as the
                     other seat dialogs: the deflecting action leads filled and
                     the committing one follows as an outline (see
                     seat-remove-confirm, where Cancel is filled and the confirm
                     is not). It is the action the headline offers, but it is
                     also the one that writes to their Bizzabo ticket. -->
                <button
                    type="button"
                    data-test="seat-full-join"
                    class="inline-flex items-center gap-[8px] rounded-[50px] px-[20px] py-[10px]
                        border border-(--magenta) text-(--magenta)
                        font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] cursor-pointer
                        transition-colors duration-200 hover:bg-(--magenta) hover:text-white
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)"
                    @click="handleJoin"
                >
                    <TimeCircle class="size-[14px] shrink-0" />
                    {{ joinLabel }}
                </button>

                <!-- "Add to My Schedule" used to sit here as a separate way out:
                     take the session without a seat. It is folded into the
                     affirmative above now — joining standby adds it anyway — so
                     keeping it offered the same add while quietly dropping the
                     standby place the attendee opened this screen for. -->
            </div>
        </div>

        <div class="flex justify-center mt-[25px]">
            <button
                type="button"
                data-test="seat-full-close"
                class="bg-transparent border-none p-0 cursor-pointer
                    font-hubspot-sans font-light text-[14px] leading-[1.5] tracking-[0.28px]
                    text-(--darker-pink) underline decoration-dotted decoration-[8.5%] underline-offset-[3px]
                    transition-colors duration-200 hover:text-(--purple) hover:no-underline"
                @click="open = false"
            >Close</button>
        </div>
    </FragmentsModalShell>
</template>
